import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type { RequestParams, ApiResponse } from '@/shared/api';
import type { ApiResponseList } from '@/shared/api/types';
import { GEO_ZONE_KEYS } from '../lib';
import { GeoZoneApi } from '../api';
import type {
  GeoZoneBase,
  GeoZoneById,
  GeoZoneCreatePayload,
  GeoZoneUpdatePayload,
  GeoZoneItem,
  GeoZoneItemListParams,
  GeoZonePolygonListItem,
} from './types';

export const useGeoZoneItemList = (params?: GeoZoneItemListParams) => {
  const hasNameFilter = Boolean(params?.name && params.name.trim().length >= 1);
  const hasIdsFilter = Boolean(params?.ids && params.ids.length > 0);

  return useQuery<ApiResponse<GeoZoneItem[]>, Error, GeoZoneItem[] | undefined>({
    queryKey: GEO_ZONE_KEYS.itemList(params),
    queryFn: () => GeoZoneApi.getItemList(params),
    enabled: hasNameFilter || hasIdsFilter,
    placeholderData: keepPreviousData,
    select: (data) => data.result ?? [],
  });
};

export const useGeoZonePolygonList = () => {
  return useQuery<ApiResponse<GeoZonePolygonListItem[]>, Error, GeoZonePolygonListItem[]>({
    queryKey: GEO_ZONE_KEYS.polygonList(),
    queryFn: () => GeoZoneApi.getPolygonList(),
    select: (data) => data.result ?? [],
  });
};

export const useGeoZoneList = (params?: RequestParams) => {
  return useQuery<ApiResponseList<GeoZoneBase>>({
    queryKey: GEO_ZONE_KEYS.list(params),
    queryFn: () => GeoZoneApi.getList(params),
    placeholderData: keepPreviousData,
  });
};

export const useGeoZoneById = (id: number) => {
  return useQuery<ApiResponse<GeoZoneById>>({
    queryKey: GEO_ZONE_KEYS.detail(id),
    queryFn: () => GeoZoneApi.getById(id),
    enabled: !!id,
  });
};

export const useCreateGeoZone = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: GeoZoneCreatePayload) => GeoZoneApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GEO_ZONE_KEYS.all });
    },
  });
};

export const useUpdateGeoZone = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: GeoZoneUpdatePayload) => GeoZoneApi.update(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GEO_ZONE_KEYS.all });
    },
  });
};

export const useDeleteGeoZone = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => GeoZoneApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GEO_ZONE_KEYS.all });
    },
  });
};
